import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Session Service for managing user sessions
 * Tracks active user sessions across devices for security and monitoring
 */

// ===== SESSION MANAGEMENT FUNCTIONS =====

/**
 * Creates a new session when user logs in
 * @param {string} userId - User ID
 * @param {Object} sessionData - Session information
 * @returns {Promise<string>} Session ID
 */
export const createSession = async (userId, sessionData = {}) => {
  try {
    const sessionDoc = {
      userId,
      loginTime: Timestamp.now(),
      lastActivity: Timestamp.now(),
      isActive: true,
      device: getDeviceInfo(),
      browser: getBrowserInfo(),
      ipAddress: await getClientIP(),
      userAgent: navigator.userAgent,
      location: sessionData.location || null,
      sessionToken: generateSessionToken(),
      createdAt: Timestamp.now(),
      ...sessionData
    };
    
    const docRef = await addDoc(collection(db, 'user_sessions'), sessionDoc);
    
    // Store session ID in sessionStorage for tracking
    sessionStorage.setItem('currentSessionId', docRef.id);
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating session:', error);
    throw new Error('Error al crear la sesión');
  }
};

/**
 * Updates session activity timestamp
 * @param {string} sessionId - Session ID to update
 * @returns {Promise<boolean>} Success status
 */
export const updateSessionActivity = async (sessionId) => {
  try {
    if (!sessionId) return false;
    
    const sessionRef = doc(db, 'user_sessions', sessionId);
    await updateDoc(sessionRef, {
      lastActivity: Timestamp.now()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating session activity:', error);
    return false;
  }
};

/**
 * Ends a specific session
 * @param {string} sessionId - Session ID to end
 * @returns {Promise<boolean>} Success status
 */
export const endSession = async (sessionId) => {
  try {
    if (!sessionId) return false;
    
    const sessionRef = doc(db, 'user_sessions', sessionId);
    await updateDoc(sessionRef, {
      isActive: false,
      endTime: Timestamp.now(),
      lastActivity: Timestamp.now()
    });
    
    // Clear from sessionStorage if it's the current session
    const currentSessionId = sessionStorage.getItem('currentSessionId');
    if (currentSessionId === sessionId) {
      sessionStorage.removeItem('currentSessionId');
    }
    
    return true;
  } catch (error) {
    console.error('Error ending session:', error);
    return false;
  }
};

/**
 * Ends all sessions for a specific user
 * @param {string} userId - User ID
 * @returns {Promise<boolean>} Success status  
 */
export const endAllUserSessions = async (userId) => {
  try {
    // Use simple query to avoid composite index requirement
    const q = query(
      collection(db, 'user_sessions'),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    
    // Filter active sessions in JavaScript and create update promises
    const updatePromises = querySnapshot.docs
      .filter(doc => doc.data().isActive === true)
      .map(doc => 
        updateDoc(doc.ref, {
          isActive: false,
          endTime: Timestamp.now(),
          lastActivity: Timestamp.now()
        })
      );
    
    await Promise.all(updatePromises);
    
    // Clear from sessionStorage if user is logging out their own sessions
    sessionStorage.removeItem('currentSessionId');
    
    return true;
  } catch (error) {
    console.error('Error ending all user sessions:', error);
    return false;
  }
};

/**
 * Gets all active sessions for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of active sessions
 */
export const getUserActiveSessions = async (userId) => {
  try {
    // Use simple query to avoid composite index requirement
    const q = query(
      collection(db, 'user_sessions'),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const currentSessionId = sessionStorage.getItem('currentSessionId');
    
    let sessions = querySnapshot.docs.map(doc => {
      const data = { id: doc.id, ...doc.data() };
      
      // Mark current session
      data.isCurrentSession = doc.id === currentSessionId;
      
      return data;
    });
    
    // Filter and sort in JavaScript to avoid composite index
    sessions = sessions
      .filter(session => session.isActive === true)
      .sort((a, b) => {
        const timeA = a.loginTime?.toDate ? a.loginTime.toDate() : new Date(a.loginTime || 0);
        const timeB = b.loginTime?.toDate ? b.loginTime.toDate() : new Date(b.loginTime || 0);
        return timeB - timeA; // Descending order
      });
    
    return sessions;
  } catch (error) {
    console.error('Error getting user active sessions:', error);
    return [];
  }
};

/**
 * Gets all active sessions (admin function)
 * @returns {Promise<Array>} Array of all active sessions
 */
export const getAllActiveSessions = async () => {
  try {
    // Use simple query to avoid composite index requirement
    const q = query(collection(db, 'user_sessions'));
    
    const querySnapshot = await getDocs(q);
    const currentSessionId = sessionStorage.getItem('currentSessionId');
    
    let sessions = querySnapshot.docs.map(doc => {
      const data = { id: doc.id, ...doc.data() };
      
      // Mark current session
      data.isCurrentSession = doc.id === currentSessionId;
      
      return data;
    });
    
    // Filter and sort in JavaScript to avoid composite index
    sessions = sessions
      .filter(session => session.isActive === true)
      .sort((a, b) => {
        const timeA = a.loginTime?.toDate ? a.loginTime.toDate() : new Date(a.loginTime || 0);
        const timeB = b.loginTime?.toDate ? b.loginTime.toDate() : new Date(b.loginTime || 0);
        return timeB - timeA; // Descending order
      });
    
    return sessions;
  } catch (error) {
    console.error('Error getting all active sessions:', error);
    return [];
  }
};

/**
 * Forces logout of a specific session (admin function)
 * @param {string} sessionId - Session ID to force logout
 * @param {string} adminUserId - ID of admin performing action
 * @returns {Promise<boolean>} Success status
 */
export const forceLogoutSession = async (sessionId, adminUserId) => {
  try {
    const sessionRef = doc(db, 'user_sessions', sessionId);
    await updateDoc(sessionRef, {
      isActive: false,
      endTime: Timestamp.now(),
      lastActivity: Timestamp.now(),
      forceLogoutBy: adminUserId,
      forceLogoutAt: Timestamp.now()
    });
    
    return true;
  } catch (error) {
    console.error('Error forcing logout session:', error);
    return false;
  }
};

/**
 * Cleans up inactive sessions based on lastActivity
 * @param {number} hoursInactive - Number of hours of inactivity to consider session dead
 * @returns {Promise<number>} Number of sessions cleaned up
 */
export const cleanupInactiveSessions = async (hoursInactive = 24) => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setHours(cutoffDate.getHours() - hoursInactive);
    const cutoffTimestamp = Timestamp.fromDate(cutoffDate);
    
    const q = query(collection(db, 'user_sessions'));
    const querySnapshot = await getDocs(q);
    
    // Filter sessions that are still marked active but haven't had activity in X hours
    const sessionsToEnd = querySnapshot.docs.filter(doc => {
      const data = doc.data();
      return data.isActive === true && 
             data.lastActivity && 
             data.lastActivity.toMillis() < cutoffTimestamp.toMillis();
    });
    
    // Mark these sessions as inactive
    const updatePromises = sessionsToEnd.map(doc => 
      updateDoc(doc.ref, {
        isActive: false,
        endTime: Timestamp.now(),
        endReason: 'inactive_cleanup'
      })
    );
    
    await Promise.all(updatePromises);
    
    console.log(`Cleaned up ${sessionsToEnd.length} inactive sessions`);
    return sessionsToEnd.length;
  } catch (error) {
    console.error('Error cleaning up inactive sessions:', error);
    return 0;
  }
};

/**
 * Cleans up old inactive sessions (maintenance function)
 * @param {number} daysOld - Days old to clean up (default 30)
 * @returns {Promise<number>} Number of sessions cleaned
 */
export const cleanupOldSessions = async (daysOld = 30) => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);
    const cutoffTimestamp = Timestamp.fromDate(cutoffDate);
    
    // Use simple query to avoid composite index requirement
    const q = query(collection(db, 'user_sessions'));
    
    const querySnapshot = await getDocs(q);
    
    // Filter in JavaScript to avoid composite index
    const sessionsToDelete = querySnapshot.docs.filter(doc => {
      const data = doc.data();
      return data.isActive === false && 
             data.endTime && 
             data.endTime.toMillis() < cutoffTimestamp.toMillis();
    });
    
    const deletePromises = sessionsToDelete.map(doc => deleteDoc(doc.ref));
    
    await Promise.all(deletePromises);
    
    return sessionsToDelete.length;
  } catch (error) {
    console.error('Error cleaning up old sessions:', error);
    return 0;
  }
};

// ===== SESSION MONITORING FUNCTIONS =====

/**
 * Sets up real-time session monitoring for a user
 * @param {string} userId - User ID to monitor
 * @param {Function} callback - Callback function for session updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToUserSessions = (userId, callback) => {
  try {
    // Use simple query to avoid composite index requirement
    const q = query(
      collection(db, 'user_sessions'),
      where('userId', '==', userId)
    );
    
    return onSnapshot(q, (querySnapshot) => {
      const currentSessionId = sessionStorage.getItem('currentSessionId');
      
      let sessions = querySnapshot.docs.map(doc => {
        const data = { id: doc.id, ...doc.data() };
        data.isCurrentSession = doc.id === currentSessionId;
        return data;
      });
      
      // Filter and sort in JavaScript to avoid composite index
      sessions = sessions
        .filter(session => session.isActive === true)
        .sort((a, b) => {
          const timeA = a.loginTime?.toDate ? a.loginTime.toDate() : new Date(a.loginTime || 0);
          const timeB = b.loginTime?.toDate ? b.loginTime.toDate() : new Date(b.loginTime || 0);
          return timeB - timeA; // Descending order
        });
      
      callback(sessions);
    });
  } catch (error) {
    console.error('Error subscribing to user sessions:', error);
    return () => {}; // Return empty unsubscribe function
  }
};

/**
 * Sets up real-time monitoring for all active sessions (admin)
 * @param {Function} callback - Callback function for session updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAllSessions = (callback) => {
  try {
    // Use simple query to avoid composite index requirement
    const q = query(collection(db, 'user_sessions'));
    
    return onSnapshot(q, (querySnapshot) => {
      const currentSessionId = sessionStorage.getItem('currentSessionId');
      
      let sessions = querySnapshot.docs.map(doc => {
        const data = { id: doc.id, ...doc.data() };
        data.isCurrentSession = doc.id === currentSessionId;
        return data;
      });
      
      // Filter and sort in JavaScript to avoid composite index
      sessions = sessions
        .filter(session => session.isActive === true)
        .sort((a, b) => {
          const timeA = a.loginTime?.toDate ? a.loginTime.toDate() : new Date(a.loginTime || 0);
          const timeB = b.loginTime?.toDate ? b.loginTime.toDate() : new Date(b.loginTime || 0);
          return timeB - timeA; // Descending order
        });
      
      callback(sessions);
    });
  } catch (error) {
    console.error('Error subscribing to all sessions:', error);
    return () => {}; // Return empty unsubscribe function
  }
};

// ===== UTILITY FUNCTIONS =====

/**
 * Gets device information from user agent
 * @returns {string} Device information
 */
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  
  if (/Mobile|Android|iPhone|iPad/.test(userAgent)) {
    if (/iPhone/.test(userAgent)) return 'iPhone';
    if (/iPad/.test(userAgent)) return 'iPad';
    if (/Android/.test(userAgent)) return 'Android Device';
    return 'Mobile Device';
  }
  
  if (/Windows/.test(userAgent)) return 'Windows PC';
  if (/Mac/.test(userAgent)) return 'Mac';
  if (/Linux/.test(userAgent)) return 'Linux PC';
  
  return 'Desktop Computer';
};

/**
 * Gets browser information from user agent
 * @returns {string} Browser information
 */
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  
  if (/Chrome/.test(userAgent) && !/Edge/.test(userAgent)) return 'Chrome';
  if (/Firefox/.test(userAgent)) return 'Firefox';
  if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) return 'Safari';
  if (/Edge/.test(userAgent)) return 'Edge';
  if (/Opera/.test(userAgent)) return 'Opera';
  
  return 'Unknown Browser';
};

/**
 * Gets client IP address (simplified for local development)
 * @returns {Promise<string>} IP address
 */
const getClientIP = async () => {
  try {
    // For production, consider using a service like ipapi.co
    // For local development, return local network indicator
    return 'Local Network';
  } catch (error) {
    return 'Unknown';
  }
};

/**
 * Generates a secure session token
 * @returns {string} Session token
 */
const generateSessionToken = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  
  for (let i = 0; i < 64; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return token;
};

/**
 * Formats session duration
 * @param {Date|Timestamp} loginTime - Session start time
 * @returns {string} Formatted duration
 */
export const formatSessionDuration = (loginTime) => {
  const now = new Date();
  const login = loginTime instanceof Date ? loginTime : loginTime.toDate();
  const diffMs = now - login;
  
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (diffHours > 24) {
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ${diffHours % 24}h`;
  }
  
  if (diffHours > 0) {
    return `${diffHours}h ${diffMinutes}m`;
  }
  
  return `${diffMinutes}m`;
};

/**
 * Formats time since last activity
 * @param {Date|Timestamp} lastActivity - Last activity time
 * @returns {string} Formatted time since activity
 */
export const formatTimeSinceActivity = (lastActivity) => {
  const now = new Date();
  const activity = lastActivity instanceof Date ? lastActivity : lastActivity.toDate();
  const diffMs = now - activity;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  
  if (diffMinutes < 1) {
    return 'Ahora mismo';
  } else if (diffMinutes < 60) {
    return `Hace ${diffMinutes}m`;
  } else {
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      return `Hace ${diffHours}h`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `Hace ${diffDays}d`;
    }
  }
};

/**
 * Gets current session ID from storage
 * @returns {string|null} Current session ID
 */
export const getCurrentSessionId = () => {
  return sessionStorage.getItem('currentSessionId');
};

/**
 * Validates if current session is still active
 * @returns {Promise<boolean>} Session validity
 */
export const validateCurrentSession = async () => {
  try {
    const sessionId = getCurrentSessionId();
    if (!sessionId) return false;
    
    const sessionRef = doc(db, 'user_sessions', sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (!sessionDoc.exists()) return false;
    
    const sessionData = sessionDoc.data();
    return sessionData.isActive === true;
  } catch (error) {
    console.error('Error validating current session:', error);
    return false;
  }
};